export const createWorkOrderSearchFieldOptions = (contentLabel = '内容') => ([
  { label: '全部字段', value: 'ALL' },
  { label: '工单编号', value: 'WORK_ORDER_NO' },
  { label: '工单ID', value: 'ID' },
  { label: '客户/门店', value: 'CUSTOMER_NAME' },
  { label: '产品名称', value: 'PRODUCT_NAME' },
  { label: '分类标签', value: 'CATEGORY' },
  { label: '授权码/地址', value: 'STORE_AUTH_CODE' },
  { label: '联系人', value: 'CONTACT' },
  { label: '联系电话', value: 'PHONE' },
  { label: contentLabel, value: 'CONTENT' }
])

export const getWorkOrderSearchPlaceholder = (searchField, options) => {
  const current = options.find((item) => item.value === searchField)
  return current ? `按${current.label}搜索` : '请输入搜索关键词'
}

export const buildWorkOrderSearchText = (item, searchField, options = {}) => {
  const { includeCategories = false } = options
  const tags = includeCategories ? (item.categories || []).map((tag) => tag.name).join(' ') : ''
  const contentText = [
    item.description,
    item.serviceContent,
    item.processResult,
    item.handlingMethod,
    item.summary,
    tags
  ].filter(Boolean).join(' ')

  switch (searchField) {
    case 'WORK_ORDER_NO':
      return item.workOrderNo || ''
    case 'ID':
      return item.id != null ? String(item.id) : ''
    case 'CUSTOMER_NAME':
      return item.customerName || ''
    case 'PRODUCT_NAME':
      return [item.productName, item.productModel].filter(Boolean).join(' ')
    case 'CATEGORY':
      return tags
    case 'STORE_AUTH_CODE':
      return item.storeAuthCode || ''
    case 'CONTACT':
      return item.contact || ''
    case 'PHONE':
      return item.phone || ''
    case 'CONTENT':
      return contentText
    default:
      return [
        item.workOrderNo,
        item.id != null ? String(item.id) : '',
        item.customerName,
        item.productName,
        item.productModel,
        item.storeAuthCode,
        item.contact,
        item.phone,
        contentText
      ].filter(Boolean).join(' ')
  }
}

const normalizeKeyword = (value) => String(value || '').trim().toLowerCase()

const isExactMatchField = (searchField) => searchField === 'WORK_ORDER_NO' || searchField === 'ID'

export const matchesWorkOrderKeyword = (item, searchField, keyword, options = {}) => {
  const normalizedKeyword = normalizeKeyword(keyword)
  if (!normalizedKeyword) return true
  const searchText = normalizeKeyword(buildWorkOrderSearchText(item, searchField, options))
  if (isExactMatchField(searchField)) {
    return searchText === normalizedKeyword
  }
  return searchText.includes(normalizedKeyword)
}

export const normalizeWorkOrderStatus = (status) => {
  if (status === 'PENDING') return 'PENDING'
  if (status === 'PROCESSING') return 'PROCESSING'
  if (status === 'COMPLETED' || status === 'CLOSED' || status === 'CANCELLED') return 'CLOSED'
  return status || ''
}

export const createWorkOrderStatusOptions = () => ([
  { label: '未处理', value: 'PENDING' },
  { label: '处理中', value: 'PROCESSING' },
  { label: '已结单', value: 'CLOSED' }
])

export const matchesWorkOrderStatusFilter = (status, filterValue) => {
  if (!filterValue) return true
  return normalizeWorkOrderStatus(status) === filterValue
}

export const getWorkOrderStatusText = (status) => ({
  PENDING: '未处理',
  PROCESSING: '处理中',
  CLOSED: '已结单'
}[normalizeWorkOrderStatus(status)] || status || '未知')

export const getWorkOrderStatusType = (status) => ({
  PENDING: 'warning',
  PROCESSING: 'primary',
  CLOSED: 'success'
}[normalizeWorkOrderStatus(status)] || 'info')

const formatDuration = (ms) => {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `${minutes} 分钟`
  return `${hours} 小时 ${minutes} 分钟`
}

const isShipmentTicket = (row) => {
  const text = [
    row?.handlingMethod,
    row?.processResult,
    row?.serviceContent,
    row?.summary
  ].filter(Boolean).join(' ')
  return /邮寄|补发|快递|物流|寄送|发配件|寄配件/i.test(text)
}

export const getWorkOrderSlaMeta = (row, now = Date.now()) => {
  if (!row) return { type: 'info', label: '暂无', subtext: '—', detail: '—' }

  const reminderHours = isShipmentTicket(row) ? 24 : 6
  const dangerHours = reminderHours * 2
  const reminderMs = reminderHours * 3600000
  const dangerMs = dangerHours * 3600000
  const status = normalizeWorkOrderStatus(row.status)

  if (status === 'PENDING') {
    const base = row.reportTime || row.createdAt
    if (!base) return { type: 'info', label: '未处理', subtext: '缺少报单时间', detail: '缺少报单时间' }
    const elapsed = now - new Date(base).getTime()
    if (elapsed >= dangerMs) {
      return {
        type: 'danger',
        label: `已超过${dangerHours}小时未处理`,
        subtext: `已过去 ${formatDuration(elapsed)}`,
        detail: `工单自报单后已过去 ${formatDuration(elapsed)}，已经超过 ${dangerHours} 小时未处理`
      }
    }
    if (elapsed >= reminderMs) {
      return {
        type: 'warning',
        label: `已超过${reminderHours}小时未处理`,
        subtext: `已过去 ${formatDuration(elapsed)}`,
        detail: `工单自报单后已过去 ${formatDuration(elapsed)}，已经超过 ${reminderHours} 小时未处理`
      }
    }
    const remain = reminderMs - elapsed
    return {
      type: 'success',
      label: `已过去 ${formatDuration(elapsed)}`,
      subtext: `距${reminderHours}小时提醒还剩 ${formatDuration(remain)}`,
      detail: `工单自报单后已过去 ${formatDuration(elapsed)}，距离 ${reminderHours} 小时提醒还剩 ${formatDuration(remain)}`
    }
  }

  if (status === 'PROCESSING') {
    const base = row.updatedAt || row.reportTime || row.createdAt
    if (!base) return { type: 'info', label: '处理中', subtext: '缺少更新时间', detail: '缺少更新时间' }
    const elapsed = now - new Date(base).getTime()
    if (elapsed >= dangerMs) {
      return {
        type: 'danger',
        label: `已超过${dangerHours}小时未变更`,
        subtext: `已过去 ${formatDuration(elapsed)}`,
        detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，已经超过 ${dangerHours} 小时未变更`
      }
    }
    if (elapsed >= reminderMs) {
      return {
        type: 'warning',
        label: `已超过${reminderHours}小时未变更`,
        subtext: `已过去 ${formatDuration(elapsed)}`,
        detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，已经超过 ${reminderHours} 小时未变更`
      }
    }
    const remain = reminderMs - elapsed
    return {
      type: 'primary',
      label: `已过去 ${formatDuration(elapsed)}`,
      subtext: `距${reminderHours}小时提醒还剩 ${formatDuration(remain)}`,
      detail: `工单自上次处理变更后已过去 ${formatDuration(elapsed)}，距离 ${reminderHours} 小时提醒还剩 ${formatDuration(remain)}`
    }
  }

  if (status === 'CLOSED') {
    return { type: 'info', label: '已结单', subtext: '工单已闭环', detail: '工单已结单闭环' }
  }
  return { type: 'info', label: '待确认', subtext: '—', detail: '—' }
}
